<template>
  <v-container class="insurance-quotes-container">
    <v-row no-gutters class="mt-sm-6">
      <v-col cols="12" class="section-left">
        <v-row no-gutters>
          <v-col cols="12" sm="1">
            <router-link
              :to="{ name: 'GetFreeInsuranceQuote' }"
              class="back-arrow"
              ><v-icon large color="mid-grey" class="mt-1">{{
                icons.mdiArrowLeft
              }}</v-icon></router-link
            >
          </v-col>
          <v-col cols="12" sm="11">
            <h1>Your will is ready!</h1>
            <p class="mt-2 mt-sm-6 main-p">
              You also have exclusive access to
              {{
                whitelabel.brand == 'quickwill' ? 'QuickWill’s' : 'Old Mutual’s'
              }}
              personalised and affordable products to cover your costs.
            </p>
            <h2 class="mt-2 mt-md-8">Prepare for life’s uncertainties</h2>
            <p class="mt-sm-6 sub-p">
              Safeguard your family from the unexpected costs of death. Don't
              leave their tomorrow to chance.
            </p>
          </v-col>
          <v-col cols="12" sm="2"></v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row no-gutters class="mt-sm-6" v-if="calculatedQuotes.length">
      <v-col cols="12" sm="1"></v-col>
      <v-col cols="12" sm="11">
        <v-col cols="12" style="padding: 0">
          <v-divider style="" class="insurance-divider" />
        </v-col>
        <div class="insurance-asset-block">
          <v-row no-gutters class="container-lg">
            <v-col cols="12" md="6">
              <v-col
                :class="'card-container' + ' ' + 'container-left-' + index"
                v-for="(quote, index) in calculatedQuotesFirstTwo"
                :key="'first-' + index"
                cols="12"
                md="12"
              >
                <InsuranceCard
                  :quote="quote"
                  :people-relations="peopleRelations"
                ></InsuranceCard>
              </v-col>
            </v-col>
            <v-col cols="12" md="6">
              <v-col
                :class="'card-container' + ' ' + 'container-right-' + index"
                v-for="(quote, index) in calculatedQuotesLastTwo"
                :key="'third-' + index"
                cols="12"
                md="12"
              >
                <InsuranceCard
                  :quote="quote"
                  :people-relations="peopleRelations"
                ></InsuranceCard>
              </v-col>
            </v-col>
          </v-row>
          <v-row no-gutters class="container-small">
            <v-col cols="12" md="6">
              <v-col
                class="card-container"
                v-for="(quote, index) in calculatedQuotesSmallFirstTwo"
                :key="'first-' + index"
                cols="12"
                md="12"
              >
                <InsuranceCard
                  :quote="quote"
                  :people-relations="peopleRelations"
                ></InsuranceCard>
              </v-col>
            </v-col>
            <v-col cols="12" md="6">
              <v-col
                class="card-container"
                v-for="(quote, index) in calculatedQuotesSmallLastTwo"
                :key="'third-' + index"
                cols="12"
                md="12"
              >
                <InsuranceCard
                  :quote="quote"
                  :people-relations="peopleRelations"
                ></InsuranceCard>
              </v-col>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col class="card-container" cols="0" lg="6"></v-col>
            <v-col class="card-container" cols="12" lg="6">
              <v-btn
                rounded
                depressed
                large
                color="primary"
                class="btn-action"
                :loading="buttonLoadingNext"
                :disabled="buttonLoading"
                @click="submit"
                ><span>Yes I'm Interested</span
                ><v-icon small>{{ icons.mdiArrowRight }}</v-icon></v-btn
              >
              <v-btn
                rounded
                depressed
                large
                color="grey"
                class="btn-action"
                :loading="buttonLoadingSkipToWill"
                :disabled="buttonLoading"
                @click="skip"
                ><span>No thanks, I'll skip to my will</span></v-btn
              >
            </v-col>
          </v-row>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import { TESTATOR_STEPS_QUERY } from '../../constants/graphql/overview';
  import OverviewProgressBars from '../overview/OverviewProgressBars.vue';
  import InsuranceCard from './InsuranceCard.vue';
  import Steps from '../../helpers/steps';

  export default {
    name: 'InsuranceQuotes',
    components: {
      OverviewProgressBars,
      InsuranceCard,
    },
    props: {
      testator: {
        required: true,
        type: Object,
      },
      quotes: {
        required: true,
        type: Array,
      },
      buttonLoading: {
        default: false,
        type: Boolean,
      },
      peopleRelations: {
        type: Object,
        required: true,
      },
    },
    watch: {
      buttonLoading: function (newValue) {
        if (!newValue) {
          this.buttonLoadingNext = newValue;
        }
      },
    },
    created() {
      if (!this.quotes.length) {
        this.requestAQuote();
      }
    },
    data() {
      return {
        testatorSteps: {},
        enabledFeatures: [],
        buttonLoadingSkipToWill: false,
        buttonLoadingNext: false,
      };
    },
    computed: {
      insurance() {
        return this.testator.insurance;
      },
      steps() {
        return Steps.getCalculatedSteps(
          this.testator,
          this.testatorSteps,
          this.enabledFeatures,
          this.whitelabel.images
        );
      },
      educationQuotes() {
        return this.quotes.filter(quote => {
          return quote.productCode === 'EC_D';
        });
      },
      calculatedQuotes() {
        return this.quotes
          .map(quote => {
            return {
              ...quote,
              index: this.indexForQuote(quote),
            };
          })
          .sort((a, b) => {
            const productCodeOrder = ['EC_D', 'DAC', 'COC', 'CAC'];
            const indexA = productCodeOrder.indexOf(a.productCode);
            const indexB = productCodeOrder.indexOf(b.productCode);
            return indexA - indexB;
          })
          .filter(item => item.childrenQuotes !== '[]');
      },
      calculatedQuotesFirstTwo() {
        return this.calculatedQuotes.slice(0, 2);
      },
      calculatedQuotesLastTwo() {
        return this.calculatedQuotes.slice(2, 4);
      },
      calculatedQuotesSmall() {
        return this.quotes
          .map(quote => {
            return {
              ...quote,
              index: this.indexForQuote(quote),
            };
          })
          .sort((a, b) => {
            const productCodeOrder = ['EC_D', 'COC', 'CAC', 'DAC'];
            const indexA = productCodeOrder.indexOf(a.productCode);
            const indexB = productCodeOrder.indexOf(b.productCode);
            return indexA - indexB;
          })
          .filter(item => item.childrenQuotes !== '[]');
      },
      calculatedQuotesSmallFirstTwo() {
        return this.calculatedQuotesSmall.slice(0, 2);
      },
      calculatedQuotesSmallLastTwo() {
        return this.calculatedQuotesSmall.slice(2, 4);
      },
    },
    methods: {
      indexForQuote(quote) {
        switch (quote.productCode) {
          case 'EC_D':
            return 0;
          case 'COC':
            return 1;
          case 'CAC':
            return 2;
          case 'DAC':
            return 3;
          default:
            break;
        }
      },
      submit() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'insurance-interested-buy-btn',
          omuserid: this.$store.getters?.userId,
        });
        this.buttonLoadingNext = true;
        this.$emit('startLead');
      },
      requestAQuote() {
        const insuranceInputDetails = {
          testatorId: this.testator.id,
          monthlyIncome: parseInt(this.insurance.monthlyIncome),
          smoker: this.insurance.smoker,
          educationLevel: this.insurance.educationLevel,
        };
        const params = {
          insuranceInputDetails: insuranceInputDetails,
        };

        this.$emit('updateInsuranceAndRequestQuotes', params);
      },
      skip() {
        this.buttonLoadingSkipToWill = true;
        const insuranceInputDetails = {
          testatorId: this.testator.id,
          skipInsurance: true,
        };
        const params = {
          insuranceInputDetails: insuranceInputDetails,
          redirectLocation: 'OverviewPage',
        };
        this.$emit('updateInsurance', params);
      },
    },
    apollo: {
      testatorSteps: {
        query: TESTATOR_STEPS_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'network-only',
        update: data => data.testator.steps,
        result(response) {
          if (response.data) {
            this.enabledFeatures = response.data.testator.enabledFeatures;
          }
        },
      },
    },
  };
</script>

<style lang="scss">
  .insurance-quotes-container {
    .insurance-divider {
      border: 1px solid var(--v-primary-base);
    }

    .section-left .main-p {
      font-size: 24px;
    }

    .section-left .sub-p {
      font-size: 20px;
    }

    .insurance-asset-block {
      // border in global.scss because of IE issue
      border: none;

      padding: 15px 20px;

      margin-bottom: 20px;
      padding: 20px;
      padding-left: 0;
      padding-right: 0;
      .card-container {
        padding: 0;
      }
      .container-left-0 {
        padding-right: 16px;
        padding-bottom: 16px;
      }
      .container-left-1 {
        padding-right: 16px;
        padding-top: 16px;
        padding-bottom: 16px;
      }
      .container-right-0 {
        padding-left: 16px;
        padding-bottom: 16px;
      }
      .container-right-1 {
        padding-left: 16px;
        padding-top: 16px;
        padding-bottom: 16px;
      }
    }

    .btn-action {
      font-size: 15px;
    }
  }

  .quote-container {
    min-height: 420px;

    .quote-title {
      font-size: 20px;
    }
    .cost {
      color: var(--v-cost-orange-base);
    }
    .payout-section {
      span {
        font-size: 15px;
      }

      .payout-title {
        span {
          color: black;
          span {
            color: var(--v-quote-payout-green-base);
            font-weight: 600;
          }
        }
      }
    }
  }

  @media screen and (max-width: 1263px) and (min-width: 960px) {
    .quote-container {
      min-height: 550px;
    }
  }

  @media screen and (max-width: 960px) and (min-width: 0px) {
    .quote-container {
      min-height: auto;
    }

    .insurance-quotes-container {
      .container-lg {
        display: none;
      }

      h1 {
        font-size: 20px;
      }
      h2 {
        font-size: 16px;
      }
      .section-left .main-p {
        font-size: 16px;
      }
      .section-left .sub-p {
        font-size: 12px;
      }

      .insurance-asset-block {
        padding: 0;
        padding-top: 12px;
        padding-bottom: 50px;

        .card-container {
          padding-top: 12px;
        }
      }

      .btn-action {
        font-size: 12px;
      }
    }
  }
  @media screen and (min-width: 961px) {
    .insurance-quotes-container {
      .container-small {
        display: none;
      }
    }
  }
</style>
